import { useEffect } from 'react'

import { FormattedMessage } from 'react-intl'

import { useNavigation } from '@subaio/components'
import { RequestError } from '@subaio/utils'

import type { UserLogin } from 'apiTypes/UserLogin'

import { asContainer } from 'lib/container'

import { AuthenticationForm } from './AuthenticationForm'
import { Expired } from './Expired'
import { getLocaleConfiguration, legacyLocaleConfiguration } from './localeConfiguration'
import { Unauthorized } from './Unauthorized'

type Params = {
	sessionId: string
	country?: string
}

type State = {
	status: 'INITIAL' | 'INVALID_LOGIN' | 'INVALID_URL' | 'EXPIRED' | 'UNKNOWN_ERROR'
	localeOptions: Array<string>
}

type Options = {
	path: string
	getCountry: (params: Params) => string | undefined
}

const uuidRegex = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/

export const createAuthenticationContainer = ({ path, getCountry }: Options) =>
	asContainer<State, Params>()({
		path,
		authentication: { type: 'public' },
		initialize: async ({ sessionId, country, setLocale }) => {
			const localeConfiguration = getLocaleConfiguration(getCountry({ sessionId, country }))

			if (!localeConfiguration) {
				return {
					status: 'INVALID_URL',
					localeOptions: legacyLocaleConfiguration.localeOptions,
				}
			}

			await setLocale(localeConfiguration.defaultLocale)

			return {
				status: uuidRegex.test(sessionId) ? 'INITIAL' : 'INVALID_URL',
				localeOptions: localeConfiguration.localeOptions,
			}
		},
		Running: ({ status, sessionId, setData, setUser, api, locale, setPageTitle, setLocale, localeOptions }) => {
			const { onNavigateTo } = useNavigation()
			useEffect(() => {
				switch (status) {
					case 'INITIAL': // Fallthrough
					case 'INVALID_LOGIN': {
						setPageTitle(
							<FormattedMessage id='authentication.title' defaultMessage='Get an overview of your finances' />,
						)
						break
					}
					case 'INVALID_URL': {
						setPageTitle(<FormattedMessage id='flow.unauthorized.title' defaultMessage='Link is invalid' />)
						break
					}
					case 'EXPIRED': {
						setPageTitle(
							<FormattedMessage id='flow.expired.title' defaultMessage='Link is expired or already completed' />,
						)
						break
					}
				}
			}, [setPageTitle, status])

			switch (status) {
				case 'INITIAL': // Fallthrough
				case 'UNKNOWN_ERROR':
				case 'INVALID_LOGIN':
					return (
						<AuthenticationForm
							locale={locale}
							localeOptions={localeOptions}
							setLocale={setLocale}
							hasInvalidLogin={status === 'INVALID_LOGIN'}
							hasUnknownError={status === 'UNKNOWN_ERROR'}
							onSubmit={async (code) => {
								try {
									const { token, budgetId, brandFeatures, brandSteps, brandMetadata, redirectUrl } =
										await api.post<UserLogin>('/users/login', {
											sessionId,
											code,
											locale,
										})
									setUser({ token, budgetId, sessionId, brandFeatures, brandSteps, brandMetadata, redirectUrl })
									onNavigateTo({ name: 'Flow' })
								} catch (error) {
									if (error instanceof RequestError) {
										if (error.request.statusCode === 403) {
											setData({ status: 'EXPIRED' })
											return
										}
										if (error.request.statusCode === 401) {
											setData({ status: 'INVALID_LOGIN' })
											return
										}
									}

									setData({ status: 'UNKNOWN_ERROR' })
									throw error
								}
							}}
						/>
					)
				case 'INVALID_URL':
					return <Unauthorized />
				case 'EXPIRED':
					return <Expired />
			}
		},
	})
